import { forwardRef, useId, type FormEvent } from 'react'
import { cn } from '../utils/cn.ts'
import { Card, CardTitle, CardSubtitle, CardContent, CardAction } from '../components/Card.tsx'
import { Button } from '../components/Button.tsx'

export interface SignInCardProps {
  phone: string
  onPhoneChange: (phone: string) => void
  onSubmit: () => void
  error?: string
  loading?: boolean
  className?: string
}

export const SignInCard = forwardRef<HTMLDivElement, SignInCardProps>(
  ({ phone, onPhoneChange, onSubmit, error, loading = false, className }, ref) => {
    const errorId = useId()

    function handleSubmit(e: FormEvent) {
      e.preventDefault()
      onSubmit()
    }

    return (
      <Card ref={ref} className={cn(className)}>
        <form onSubmit={handleSubmit} className="sign-in-card-form">
          <CardTitle>Join this event</CardTitle>
          <CardSubtitle>Enter your phone number to RSVP</CardSubtitle>
          <CardContent>
            <div className="sign-in-card-field-wrapper">
              <div
                className={cn(
                  'sign-in-card-field',
                  error && 'sign-in-card-field-error',
                  loading && 'sign-in-card-field-disabled',
                )}
              >
                <span className="sign-in-card-code">+1</span>
                <span className="sign-in-card-divider" />
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="Phone number"
                  value={phone}
                  onChange={(e) => onPhoneChange(e.target.value)}
                  disabled={loading}
                  aria-label="Phone number"
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? errorId : undefined}
                  className="sign-in-card-input"
                />
              </div>
              <div className="sign-in-card-message">
                {error && (
                  <p id={errorId} role="alert" className="sign-in-card-error">
                    {error}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
          <CardAction>
            <Button type="submit" loading={loading}>
              Continue
            </Button>
          </CardAction>
        </form>
      </Card>
    )
  },
)

SignInCard.displayName = 'SignInCard'
